import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSchedulers } from './useSchedulers'
import { useChannels } from '@/api/endpoints/channels'
import { useEngagementSummary, type ChannelEngagementSummaryOut } from '@/api/endpoints/engagement'
import { usePools } from '@/api/endpoints/pools'
import {
  useSchedulableFactories,
  useStudioThumbnails,
  type ChannelConfigOut,
  type SchedulerConfigOut,
  type StudioThumbnailQueueItemOut,
} from '@/api/endpoints/scheduler'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { buildChannelCards, filterCounts } from '@/features/scheduler/channelCards'
import { ChannelBoard } from '@/features/scheduler/components/ChannelBoard'
import { ChannelDetailsDialog } from '@/features/scheduler/components/ChannelDetailsDialog'
import { ChannelEditDialog } from '@/features/scheduler/components/ChannelEditDialog'
import { ScheduleControls } from '@/features/scheduler/components/ScheduleControls'
import { StudioThumbnailsBanner, coversByChannel, isPaused } from '@/features/scheduler/components/StudioThumbnailsBanner'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import styles from './SchedulerPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

/**
 * O Scheduler: achar um canal e abrir a edição dele.
 *   - Cabeçalho com o interruptor global e os disparos "em todos os canais".
 *   - Faixa da fila de capas dos Shorts, só quando há fila.
 *   - Os pools ficam na tela Pools; aqui só os canais, e o card de um canal
 *     de pool diz de qual pool ele publica.
 *   - Grade de cards, um por canal, com a foto, a factory, a próxima geração e
 *     o que pede atenção. Clicar no card abre o modal de edição.
 *
 * Edits hit ``PATCH /scheduler/channels/{slug}/config`` (per-channel) and
 * invalidate the shared scheduler query keys so the UI reloads.
 */
export function SchedulerPage() {
  const { config, state, channels, isPending, isError, error, refetch } = useSchedulers()
  // Sem padrão ``= []`` aqui: um literal novo a cada render desfaria o useMemo.
  const { data: registeredChannels } = useChannels()
  const { data: engagementSummary } = useEngagementSummary()
  const { data: factories } = useSchedulableFactories()
  const { data: thumbnails } = useStudioThumbnails()
  const { data: pools } = usePools()
  const { active } = useActiveChannelFromUrl()
  const now = useNow()

  const [channelEdit, setChannelEdit] = useState<string | null>(null)
  // O selo "N rascunhos para aprovar" abre o modal direto na fila de posts.
  const [editQueue, setEditQueue] = useState(false)
  const [channelDetails, setChannelDetails] = useState<string | null>(null)

  const cards = useMemo(() => {
    if (!config) return []
    // Pausada pelo admin, os selos "N capas na fila" somem com a faixa.
    const queue: StudioThumbnailQueueItemOut[] = isPaused(thumbnails) ? [] : (thumbnails?.queue ?? [])
    return buildChannelCards({
      states: channels,
      configs: config.channels,
      channels: registeredChannels ?? [],
      factoryLabels: new Map((factories ?? []).map((f) => [f.slug, f.label] as const)),
      drafts: draftsBySlug(engagementSummary ?? {}),
      pendingCovers: coversByChannel(queue),
      poolNames: new Map((pools ?? []).map((p) => [p.slug, p.name] as const)),
      now,
    })
  }, [config, channels, registeredChannels, factories, engagementSummary, thumbnails, pools, now])

  if (isPending) {
    return (
      <div className={styles.page}>
        <Header />
        <p>Carregando...</p>
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.page}>
        <Header />
        <Section>
          <QueryErrorNotice error={error} label="o agendador" onRetry={refetch} />
        </Section>
      </div>
    )
  }

  const counts = filterCounts(cards)
  const registry = registeredChannels ?? []
  const nameBySlug = new Map(registry.map((c) => [c.slug, c.name] as const))
  const platformBySlug = new Map(registry.map((c) => [c.slug, c.platform] as const))
  const channelEditConfig = channelEdit && config.channels[channelEdit] ? config.channels[channelEdit] : null

  return (
    <div className={styles.page}>
      <Header
        description={
          <>
            {counts.all} {counts.all === 1 ? 'canal' : 'canais'} · {counts.active}{' '}
            {counts.active === 1 ? 'ativo' : 'ativos'}
            {counts.attention > 0 ? (
              <>
                {' · '}
                <span className={styles.attention}>
                  {counts.attention} {counts.attention === 1 ? 'pede atenção' : 'pedem atenção'}
                </span>
              </>
            ) : null}
          </>
        }
        actions={<ScheduleControls generation={config.generation} upload={config.upload} state={state} now={now} />}
      />

      <StudioThumbnailsBanner />

      <ChannelBoard
        cards={cards}
        selectedSlug={active?.slug}
        now={now}
        onEdit={(slug) => {
          setEditQueue(false)
          setChannelEdit(slug)
        }}
        onDrafts={(slug) => {
          setEditQueue(true)
          setChannelEdit(slug)
        }}
        onDetails={setChannelDetails}
      />

      {channelEditConfig ? (
        <ChannelEditDialog
          open={channelEdit !== null}
          onClose={() => {
            setChannelEdit(null)
          }}
          slug={channelEdit ?? ''}
          config={channelEditConfig}
          name={nameBySlug.get(channelEdit ?? '') ?? channelEdit ?? ''}
          platform={platformBySlug.get(channelEdit ?? '') ?? 'youtube'}
          state={channelEdit ? channels[channelEdit] : undefined}
          initialTab={editQueue ? 'posts' : 'generation'}
          focusQueue={editQueue}
        />
      ) : null}

      <ChannelDetailsDialog
        open={channelDetails !== null}
        onClose={() => {
          setChannelDetails(null)
        }}
        slug={channelDetails ?? ''}
      />
    </div>
  )
}

function Header({ description, actions }: { description?: ReactNode; actions?: ReactNode }) {
  return <PageHeader eyebrow="Automação" title="Scheduler" description={description} actions={actions} />
}

/** "hoje" e "amanhã" mudam à meia-noite: a página relê o relógio a cada minuto. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => {
      setNow(new Date())
    }, 60_000)
    return () => {
      clearInterval(t)
    }
  }, [])
  return now
}

/** Rascunhos para aprovar — só no modo "Exigir minha aprovação". */
function draftsBySlug(summary: Partial<Record<string, ChannelEngagementSummaryOut>>): Map<string, number> {
  const out = new Map<string, number>()
  for (const [slug, row] of Object.entries(summary)) {
    if (row?.posts_require_approval) out.set(slug, row.drafts)
  }
  return out
}

// Consumers: SchedulerConfigOut (dialog types)
export type { ChannelConfigOut, SchedulerConfigOut }
